import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  X,
  Search,
  Send,
  UserPlus,
  Users,
  Video,
  Calendar,
  Clock,
  Check,
  XCircle,
  Circle,
  Smile,
  ChevronLeft,
  ChevronRight,
  Bell,
  Settings,
  Loader2,
  UserCheck,
  UserX
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { cn } from '../lib/utils';
import { messengerApi } from '../api/api';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

// Common emoji list
const EMOJI_LIST = [
  '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊',
  '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋', '😛', '😜',
  '🤪', '😝', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑',
  '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤',
  '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴',
  '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁',
  '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰',
  '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👋', '🖐️',
  '✋', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '💪', '❤️', '🧡',
  '💛', '💚', '💙', '💜', '🖤', '💔', '💕', '💖', '💗', '💘'
];

// Status options
const STATUS_OPTIONS = [
  { value: 'online', label: 'Online', color: 'bg-green-500' },
  { value: 'away', label: 'Away', color: 'bg-yellow-500' },
  { value: 'offline', label: 'Offline', color: 'bg-gray-500' }
];

// Format time
function formatMessageTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function Messenger({ isOpen, onClose, onToggle }) {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('chats'); // chats, friends, requests, meetings
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [myStatus, setMyStatus] = useState('online');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    const soundEnabled = localStorage.getItem('notification_sound_enabled') !== 'false';
    if (soundEnabled) {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/sounds/message.ogg');
        }
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      } catch (err) {
        console.error('Failed to play notification sound:', err);
      }
    }
  }, []);

  // Theme classes
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-white';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-gray-50';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const buttonHover = isDarkMode ? 'hover:bg-[#2d2d44]' : 'hover:bg-gray-100';

  // Fetch data
  const fetchFriends = useCallback(async () => {
    try {
      const data = await messengerApi.getFriends();
      setFriends(data);
    } catch (err) {
      console.error('Failed to fetch friends:', err);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const data = await messengerApi.getFriendRequests();
      setFriendRequests(data);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await messengerApi.getUnreadCount();
      const prevCount = unreadCount;
      
      // Play sound if new unread messages (and messenger is not actively open with a chat)
      if (data.count > prevCount && prevCount >= 0 && !selectedFriend) {
        playNotificationSound();
        toast.info('You have new messages!', { duration: 3000 });
      }
      
      setUnreadCount(data.count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [unreadCount, selectedFriend, playNotificationSound]);

  const fetchMessages = useCallback(async (friendId) => {
    if (!friendId) return;
    try {
      const data = await messengerApi.getMessages(friendId);
      
      // Check for new incoming messages (not from current user)
      const currentUserId = localStorage.getItem('user_id');
      const incomingMessages = data.filter(m => m.from_user_id !== currentUserId);
      const prevIncomingCount = prevMessageCountRef.current;
      
      // Play sound if new incoming message arrived
      if (incomingMessages.length > prevIncomingCount && prevIncomingCount > 0) {
        playNotificationSound();
        // Show toast notification
        const newMsg = incomingMessages[incomingMessages.length - 1];
        if (newMsg) {
          toast.info(`${newMsg.from_name || 'Someone'}: ${newMsg.content?.slice(0, 50)}...`, {
            duration: 3000
          });
        }
      }
      prevMessageCountRef.current = incomingMessages.length;
      
      setMessages(data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [playNotificationSound]);

  const fetchMyStatus = useCallback(async () => {
    try {
      const data = await messengerApi.getStatus();
      setMyStatus(data.status);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      fetchFriends();
      fetchRequests();
      fetchUnreadCount();
      fetchMyStatus();
    }
  }, [isOpen, fetchFriends, fetchRequests, fetchUnreadCount, fetchMyStatus]);

  // Poll for unread count (even when closed - for notifications)
  useEffect(() => {
    // Poll every 10 seconds for new messages
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Poll for new messages when chatting
  useEffect(() => {
    if (!selectedFriend) return;
    
    const interval = setInterval(() => {
      fetchMessages(selectedFriend.id);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [selectedFriend, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Search users
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const results = await messengerApi.searchUsers(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  // Send friend request
  const handleSendFriendRequest = async (email) => {
    try {
      await messengerApi.sendFriendRequest(email);
      toast.success('Friend request sent!');
      setSearchResults(prev => prev.map(u => 
        u.email === email ? { ...u, request_pending: true } : u
      ));
      fetchRequests();
    } catch (err) {
      console.error('Friend request error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send friend request';
      toast.error(errorMessage);
    }
  };

  // Respond to friend request
  const handleRespondToRequest = async (requestId, action) => {
    try {
      await messengerApi.respondToFriendRequest(requestId, action);
      toast.success(action === 'accept' ? 'Friend added!' : 'Request declined');
      fetchRequests();
      fetchFriends();
    } catch (err) {
      toast.error('Failed to respond to request');
    }
  };

  // Update status
  const handleUpdateStatus = async (status) => {
    try {
      await messengerApi.updateStatus(status);
      setMyStatus(status);
      toast.success(`Status set to ${status}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedFriend) return;
    
    try {
      const msg = await messengerApi.sendMessage(selectedFriend.id, newMessage);
      setMessages(prev => [...prev, msg]);
      setNewMessage('');
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  // Add emoji
  const handleAddEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Open chat
  const handleOpenChat = async (friend) => {
    setSelectedFriend(friend);
    setActiveTab('chats');
    // Reset message count reference to avoid false notifications
    prevMessageCountRef.current = 0;
    await fetchMessages(friend.id);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[2];
    return <Circle className={`w-3 h-3 ${statusConfig.color} fill-current`} />;
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed right-0 top-0 h-full w-full sm:w-96 ${bgPrimary} border-l ${borderColor} shadow-2xl z-50 flex flex-col`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-3 sm:px-4 py-3 border-b ${borderColor}`}>
        <div className="flex items-center gap-2 sm:gap-3">
          <MessageCircle className="w-5 h-5 text-[#6D4AFF]" />
          <h2 className={`font-semibold ${textPrimary} text-sm sm:text-base`}>Messenger</h2>
          {unreadCount > 0 && (
            <Badge className="bg-red-500 text-white text-xs">{unreadCount}</Badge>
          )}
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Status Selector */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className={`${textMuted} ${buttonHover} gap-1 sm:gap-2 px-2`}>
                {getStatusIcon(myStatus)}
                <span className="text-xs capitalize hidden sm:inline">{myStatus}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className={`w-40 p-2 ${bgSecondary} ${borderColor}`}>
              {STATUS_OPTIONS.map(status => (
                <button
                  key={status.value}
                  onClick={() => handleUpdateStatus(status.value)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg ${buttonHover} ${textSecondary}`}
                >
                  <Circle className={`w-3 h-3 ${status.color} fill-current`} />
                  <span className="text-sm">{status.label}</span>
                  {myStatus === status.value && <Check className="w-4 h-4 ml-auto text-[#6D4AFF]" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          
          <Button variant="ghost" size="icon" onClick={onClose} className={`${textMuted} ${buttonHover}`}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${borderColor}`}>
        {[
          { id: 'chats', label: 'Chats', icon: MessageCircle },
          { id: 'friends', label: 'Friends', icon: Users },
          { id: 'requests', label: 'Requests', icon: UserPlus, count: friendRequests.incoming.length },
          { id: 'meetings', label: 'Meets', icon: Video }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              `flex-1 flex items-center justify-center gap-1 py-2.5 sm:py-3 text-xs sm:text-sm transition-colors`,
              activeTab === tab.id 
                ? `${textPrimary} border-b-2 border-[#6D4AFF]` 
                : `${textMuted} hover:${textSecondary}`
            )}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.count > 0 && (
              <Badge className="bg-red-500 text-white text-xs ml-1">{tab.count}</Badge>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Search Bar */}
        {activeTab !== 'meetings' && !selectedFriend && (
          <div className="p-3">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
              <Input
                placeholder={activeTab === 'friends' ? "Search users by email..." : "Search..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className={`pl-10 ${bgTertiary} ${borderColor} ${textPrimary}`}
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#6D4AFF]" />}
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && activeTab === 'friends' && (
          <div className={`mx-3 mb-3 p-2 rounded-lg ${bgTertiary}`}>
            <p className={`text-xs ${textMuted} mb-2`}>Search Results</p>
            {searchResults.map(user => (
              <div key={user.id} className={`flex items-center justify-between p-2 rounded-lg ${buttonHover}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                    <span className="text-white text-xs">{user.email[0].toUpperCase()}</span>
                  </div>
                  <div>
                    <p className={`text-sm ${textPrimary}`}>{user.display_name || user.email.split('@')[0]}</p>
                    <p className={`text-xs ${textMuted}`}>{user.email}</p>
                  </div>
                </div>
                {user.is_friend ? (
                  <Badge className="bg-green-500/20 text-green-500">Friends</Badge>
                ) : user.request_pending ? (
                  <Badge className="bg-yellow-500/20 text-yellow-500">Pending</Badge>
                ) : (
                  <Button size="sm" onClick={() => handleSendFriendRequest(user.email)} className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white">
                    <UserPlus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <ScrollArea className="flex-1">
          {/* Chats Tab */}
          {activeTab === 'chats' && !selectedFriend && (
            <div className="p-3 space-y-2">
              {friends.length === 0 ? (
                <div className={`text-center py-10 ${textMuted}`}>
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No friends yet</p>
                  <p className="text-sm">Search for users to add friends</p>
                </div>
              ) : (
                friends.map(friend => (
                  <button
                    key={friend.id}
                    onClick={() => handleOpenChat(friend)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg ${buttonHover} transition-colors`}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                        <span className="text-white text-sm">{friend.name[0].toUpperCase()}</span>
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 ${isDarkMode ? 'border-[#16162a]' : 'border-white'} ${
                        friend.status === 'online' ? 'bg-green-500' : 
                        friend.status === 'away' ? 'bg-yellow-500' : 'bg-gray-500'
                      }`} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-medium ${textPrimary}`}>{friend.name}</p>
                      <p className={`text-xs ${textMuted} capitalize`}>{friend.status}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${textMuted}`} />
                  </button>
                ))
              )}
            </div>
          )}

          {/* Chat View */}
          {activeTab === 'chats' && selectedFriend && (
            <div className="flex flex-col h-full">
              {/* Chat Header */}
              <div className={`flex items-center gap-3 p-3 border-b ${borderColor}`}>
                <Button variant="ghost" size="icon" onClick={() => setSelectedFriend(null)} className={`${textMuted} ${buttonHover}`}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                    <span className="text-white">{selectedFriend.name[0].toUpperCase()}</span>
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${isDarkMode ? 'border-[#16162a]' : 'border-white'} ${
                    selectedFriend.status === 'online' ? 'bg-green-500' : 
                    selectedFriend.status === 'away' ? 'bg-yellow-500' : 'bg-gray-500'
                  }`} />
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${textPrimary}`}>{selectedFriend.name}</p>
                  <p className={`text-xs ${textMuted} capitalize`}>{selectedFriend.status}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowMeetingModal(true)}
                  className={`${textMuted} ${buttonHover}`}
                  title="Schedule Meeting"
                >
                  <Video className="w-5 h-5" />
                </Button>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex",
                        msg.from_user_id === selectedFriend.id ? "justify-start" : "justify-end"
                      )}
                    >
                      <div className={cn(
                        "max-w-[80%] rounded-2xl px-4 py-2",
                        msg.from_user_id === selectedFriend.id 
                          ? `${bgTertiary} ${textPrimary}` 
                          : "bg-[#6D4AFF] text-white"
                      )}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={cn(
                          "text-xs mt-1",
                          msg.from_user_id === selectedFriend.id ? textMuted : "text-white/70"
                        )}>
                          {formatMessageTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className={`p-3 border-t ${borderColor}`}>
                <div className="flex items-center gap-2">
                  <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" className={`${textMuted} ${buttonHover}`}>
                        <Smile className="w-5 h-5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className={`w-80 p-3 ${bgSecondary} ${borderColor}`} side="top">
                      <div className="grid grid-cols-10 gap-1">
                        {EMOJI_LIST.map((emoji, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleAddEmoji(emoji)}
                            className={`p-1.5 rounded hover:${bgTertiary} text-lg`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className={`flex-1 ${bgTertiary} ${borderColor} ${textPrimary}`}
                  />
                  
                  <Button type="submit" size="icon" disabled={!newMessage.trim()} className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Friends Tab */}
          {activeTab === 'friends' && (
            <div className="p-3 space-y-2">
              {friends.length === 0 ? (
                <div className={`text-center py-10 ${textMuted}`}>
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No friends yet</p>
                  <p className="text-sm">Use the search bar to find users</p>
                </div>
              ) : (
                friends.map(friend => (
                  <div key={friend.id} className={`flex items-center gap-3 p-3 rounded-lg ${bgTertiary}`}>
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                        <span className="text-white">{friend.name[0].toUpperCase()}</span>
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${isDarkMode ? 'border-[#252540]' : 'border-gray-100'} ${
                        friend.status === 'online' ? 'bg-green-500' : 
                        friend.status === 'away' ? 'bg-yellow-500' : 'bg-gray-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${textPrimary}`}>{friend.name}</p>
                      <p className={`text-xs ${textMuted}`}>{friend.email}</p>
                    </div>
                    <Button size="sm" onClick={() => handleOpenChat(friend)} className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="p-3 space-y-4">
              {/* Incoming */}
              <div>
                <p className={`text-xs font-medium ${textMuted} mb-2`}>INCOMING REQUESTS</p>
                {friendRequests.incoming.length === 0 ? (
                  <p className={`text-sm ${textMuted} text-center py-4`}>No incoming requests</p>
                ) : (
                  friendRequests.incoming.map(req => (
                    <div key={req.id} className={`flex items-center gap-3 p-3 rounded-lg ${bgTertiary} mb-2`}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                        <span className="text-white">{req.from_name[0].toUpperCase()}</span>
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${textPrimary}`}>{req.from_name}</p>
                        <p className={`text-xs ${textMuted}`}>{req.from_email}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" onClick={() => handleRespondToRequest(req.id, 'accept')} className="h-8 w-8 bg-green-500 hover:bg-green-600 text-white">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="icon" onClick={() => handleRespondToRequest(req.id, 'reject')} className="h-8 w-8 bg-red-500 hover:bg-red-600 text-white">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <Separator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
              
              {/* Outgoing */}
              <div>
                <p className={`text-xs font-medium ${textMuted} mb-2`}>SENT REQUESTS</p>
                {friendRequests.outgoing.length === 0 ? (
                  <p className={`text-sm ${textMuted} text-center py-4`}>No pending requests</p>
                ) : (
                  friendRequests.outgoing.map(req => (
                    <div key={req.id} className={`flex items-center gap-3 p-3 rounded-lg ${bgTertiary} mb-2`}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                        <span className="text-white">{req.to_name[0].toUpperCase()}</span>
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${textPrimary}`}>{req.to_name}</p>
                        <p className={`text-xs ${textMuted}`}>{req.to_email}</p>
                      </div>
                      <Badge className="bg-yellow-500/20 text-yellow-500">Pending</Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Meetings Tab */}
          {activeTab === 'meetings' && (
            <MeetingsTab 
              friends={friends} 
              isDarkMode={isDarkMode}
              bgTertiary={bgTertiary}
              borderColor={borderColor}
              textPrimary={textPrimary}
              textMuted={textMuted}
              buttonHover={buttonHover}
            />
          )}
        </ScrollArea>
      </div>

      {/* Meeting Modal */}
      {showMeetingModal && selectedFriend && (
        <MeetingScheduler
          friend={selectedFriend}
          friends={friends}
          onClose={() => setShowMeetingModal(false)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

// Meetings Tab Component
function MeetingsTab({ friends, isDarkMode, bgTertiary, borderColor, textPrimary, textMuted, buttonHover }) {
  const [meetings, setMeetings] = useState({ created: [], invited: [] });
  const [showScheduler, setShowScheduler] = useState(false);

  // Fetch meetings on mount
  const fetchMeetingsRef = useRef(async () => {
    try {
      const data = await messengerApi.getMeetings();
      setMeetings(data);
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    }
  });

  useEffect(() => {
    const fetchData = fetchMeetingsRef.current;
    fetchData();
  }, []);

  const handleJoinMeeting = (roomUrl) => {
    window.open(roomUrl, '_blank');
  };
  
  const handleRefreshMeetings = async () => {
    try {
      const data = await messengerApi.getMeetings();
      setMeetings(data);
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    }
  };

  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-gray-50';

  return (
    <div className="p-3 space-y-4">
      <Button onClick={() => setShowScheduler(true)} className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white">
        <Video className="w-4 h-4 mr-2" />
        Schedule Meeting
      </Button>

      {/* Upcoming Meetings */}
      <div>
        <p className={`text-xs font-medium ${textMuted} mb-2`}>MY MEETINGS</p>
        {meetings.created.length === 0 && meetings.invited.length === 0 ? (
          <div className={`text-center py-8 ${textMuted}`}>
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No scheduled meetings</p>
          </div>
        ) : (
          [...meetings.created, ...meetings.invited].map(meeting => (
            <div key={meeting.id} className={`p-3 rounded-lg ${bgTertiary} mb-2`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className={`font-medium ${textPrimary}`}>{meeting.title}</p>
                  <p className={`text-xs ${textMuted}`}>
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(meeting.scheduled_time).toLocaleString()}
                  </p>
                </div>
                <Button size="sm" onClick={() => handleJoinMeeting(meeting.room_url)} className="bg-green-500 hover:bg-green-600 text-white">
                  Join
                </Button>
              </div>
              <p className={`text-xs ${textMuted}`}>
                {meeting.invitees?.length || 0} invited
              </p>
            </div>
          ))
        )}
      </div>

      {showScheduler && (
        <MeetingScheduler
          friends={friends}
          onClose={() => {
            setShowScheduler(false);
            handleRefreshMeetings();
          }}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

// Meeting Scheduler Modal
function MeetingScheduler({ friend, friends, onClose, isDarkMode }) {
  const [title, setTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('12:00');
  const [duration, setDuration] = useState(60);
  const [selectedFriends, setSelectedFriends] = useState(friend ? [friend.id] : []);
  const [loading, setLoading] = useState(false);

  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-white';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-gray-50';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  const handleSchedule = async () => {
    if (!title.trim() || selectedFriends.length === 0) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const [hours, minutes] = selectedTime.split(':');
      const scheduledTime = new Date(selectedDate);
      scheduledTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      await messengerApi.createMeeting(selectedFriends, title, scheduledTime.toISOString(), duration);
      toast.success('Meeting scheduled! Invites sent.');
      onClose();
    } catch (err) {
      toast.error('Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  const toggleFriend = (friendId) => {
    setSelectedFriends(prev => 
      prev.includes(friendId) 
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className={`w-full max-w-md mx-4 ${bgPrimary} rounded-xl border ${borderColor} overflow-hidden`}>
        <div className={`px-4 py-3 border-b ${borderColor} flex items-center justify-between`}>
          <h3 className={`font-semibold ${textPrimary}`}>Schedule Meeting</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className={`text-sm ${textMuted} mb-1 block`}>Meeting Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Team Sync"
              className={`${bgSecondary} ${borderColor} ${textPrimary}`}
            />
          </div>

          {/* Date */}
          <div>
            <label className={`text-sm ${textMuted} mb-1 block`}>Date</label>
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              fromDate={new Date()}
              className={`rounded-md border ${borderColor}`}
            />
          </div>

          {/* Time */}
          <div>
            <label className={`text-sm ${textMuted} mb-1 block`}>Time</label>
            <Input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className={`${bgSecondary} ${borderColor} ${textPrimary}`}
            />
          </div>

          {/* Duration */}
          <div>
            <label className={`text-sm ${textMuted} mb-1 block`}>Duration (minutes)</label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className={`w-full px-3 py-2 rounded-md ${bgSecondary} ${borderColor} ${textPrimary}`}
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>

          {/* Invite Friends */}
          <div>
            <label className={`text-sm ${textMuted} mb-1 block`}>Invite Friends</label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {friends.map(f => (
                <label key={f.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-100'}`}>
                  <input
                    type="checkbox"
                    checked={selectedFriends.includes(f.id)}
                    onChange={() => toggleFriend(f.id)}
                    className="rounded border-gray-400"
                  />
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center">
                    <span className="text-white text-xs">{f.name[0].toUpperCase()}</span>
                  </div>
                  <span className={`text-sm ${textPrimary}`}>{f.name}</span>
                </label>
              ))}
              {friends.length === 0 && (
                <p className={`text-sm ${textMuted} text-center py-4`}>No friends to invite</p>
              )}
            </div>
          </div>

          {/* Meeting Link Preview */}
          <div className={`p-3 rounded-lg ${bgSecondary} ${borderColor} border`}>
            <p className={`text-xs ${textMuted} mb-1`}>Meeting will be hosted on</p>
            <p className={`text-sm ${textPrimary}`}>meet.saysme.org</p>
          </div>
        </div>

        <div className={`px-4 py-3 border-t ${borderColor} flex justify-end gap-2`}>
          <Button variant="outline" onClick={onClose} className={borderColor}>Cancel</Button>
          <Button onClick={handleSchedule} disabled={loading} className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
            Schedule
          </Button>
        </div>
      </div>
    </div>
  );
}
